import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';
const UPLOAD_CONCURRENCY = 10;

const AdminEventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState('');
  const [tab, setTab] = useState('overview');

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total, duplicates, errors }
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const [evRes, visRes, phRes] = await Promise.all([
        axios.get(`/api/admin/events/${id}`),
        axios.get(`/api/admin/events/${id}/visitors`),
        axios.get(`/api/admin/events/${id}/photos`)
      ]);
      setEvent(evRes.data);
      setVisitors(visRes.data);
      setPhotos(phRes.data);
    } catch (err) {
      if (err.response?.status === 404) navigate('/admin/events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const scanPhotos = async () => {
    setScanning(true);
    setScanResult('');
    try {
      const res = await axios.post(`/api/admin/events/${id}/scan-photos`);
      setScanResult(res.data.message);
      load(); // Refresh
    } catch (err) {
      setScanResult(err.response?.data?.error || 'Scan failed.');
    } finally {
      setScanning(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Reset input so same files can be re-selected if needed
    e.target.value = '';

    setUploading(true);
    setUploadProgress({ done: 0, total: files.length, duplicates: 0, errors: [] });

    let done = 0;
    let duplicates = 0;
    const errors = [];

    // Upload in batches of UPLOAD_CONCURRENCY
    for (let i = 0; i < files.length; i += UPLOAD_CONCURRENCY) {
      const batch = files.slice(i, i + UPLOAD_CONCURRENCY);
      await Promise.all(batch.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append('photo', file);
          const res = await axios.post(
            `${API_URL}/api/admin/events/${id}/upload-photos`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          if (res.data.duplicate) duplicates++;
        } catch (err) {
          const msg = err.response?.data?.error || file.name;
          errors.push(msg);
        } finally {
          done++;
          setUploadProgress({ done, total: files.length, duplicates, errors: [...errors] });
        }
      }));
    }

    setUploading(false);
    load(); // Refresh photo count
  };

  const copyRegisterUrl = () => {
    const token = event?.register_token;
    const url = token
      ? `${window.location.origin}/em/register?token=${token}`
      : `${window.location.origin}/em/register?event_id=${id}`;
    navigator.clipboard.writeText(url);
    alert('Registration URL copied to clipboard!');
  };

  if (loading) return <LoadingScreen />;
  if (!event) return null;

  const statusColors = {
    active: { bg: '#f0fdf4', color: '#166534', label: 'Active' },
    matching: { bg: '#fef9c3', color: '#854d0e', label: 'Processing' },
    completed: { bg: '#eff6ff', color: '#1e40af', label: 'Completed' }
  };
  const sc = statusColors[event.status] || statusColors.active;

  const latestRun = event.matching_runs?.[0];

  return (
    <div style={styles.page}>
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
        <button style={styles.backBtn} onClick={() => navigate('/admin/events')}>← Events</button>
        <span style={{ fontWeight: 700, color: '#1a1a2e' }}>{event.name}</span>
        <span style={{ ...styles.statusBadge, background: sc.bg, color: sc.color }}>{sc.label}</span>
      </div>

      <div style={styles.container}>
        {/* Stats Row */}
        <div style={styles.statsRow}>
          <StatCard icon="👥" label="Visitors Registered" value={visitors.length} />
          <StatCard icon="🖼" label="Event Photos" value={photos.length} />
          <StatCard icon="✅" label="Matches Found" value={latestRun?.total_matches || 0} />
          <StatCard icon="📅" label="Event Date" value={new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} small />
        </div>

        {/* Action Buttons */}
        <div style={styles.actions}>
          <button style={styles.actionBtn} onClick={copyRegisterUrl}>
            🔗 Copy Registration URL
          </button>
          <a href={`${API_URL}/api/photos/qr/${id}`} target="_blank" rel="noreferrer">
            <button style={styles.actionBtn}>📲 View QR Code</button>
          </a>
          <button
            style={{ ...styles.actionBtn, ...(uploading ? styles.disabled : {}) }}
            onClick={() => !uploading && fileInputRef.current?.click()}
            disabled={uploading}
          >
            📤 Upload Photos
          </button>
          <button style={styles.actionBtn} onClick={() => navigate(`/admin/events/${id}/browser`)}>
            Browse Photos
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
        </div>

        {/* Upload progress */}
        {uploadProgress && (
          <div style={styles.uploadBox}>
            <div style={styles.uploadHeader}>
              <span style={{ fontWeight: '700', fontSize: '0.9rem' }}>
                {uploading
                  ? `Uploading… ${uploadProgress.done} / ${uploadProgress.total}`
                  : `Upload complete — ${uploadProgress.done - uploadProgress.duplicates - uploadProgress.errors.length} new, ${uploadProgress.duplicates} duplicate${uploadProgress.duplicates !== 1 ? 's' : ''}, ${uploadProgress.errors.length} failed`
                }
              </span>
              {!uploading && (
                <button style={styles.uploadDismiss} onClick={() => setUploadProgress(null)}>✕</button>
              )}
            </div>
            <div style={styles.progressTrack}>
              <div style={{
                ...styles.progressFill,
                width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%`,
                background: uploading ? '#4f46e5' : uploadProgress.errors.length > 0 ? '#f59e0b' : '#16a34a'
              }} />
            </div>
            {uploadProgress.errors.length > 0 && (
              <p style={styles.uploadErrors}>
                Failed: {uploadProgress.errors.slice(0, 5).join(', ')}
                {uploadProgress.errors.length > 5 ? ` +${uploadProgress.errors.length - 5} more` : ''}
              </p>
            )}
          </div>
        )}

        {scanResult && (
          <div style={styles.scanResult}>{scanResult}</div>
        )}

        {/* Tabs */}
        <div style={styles.tabs}>
          {['overview', 'visitors', 'photos', 'runs'].map(t => (
            <button
              key={t}
              style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
              onClick={() => setTab(t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
              {t === 'visitors' && ` (${visitors.length})`}
              {t === 'photos' && ` (${photos.length})`}
            </button>
          ))}
        </div>

        {/* Tab: Overview */}
        {tab === 'overview' && (
          <div style={styles.overviewGrid}>
            <InfoRow label="Event Name" value={event.name} />
            <InfoRow label="Date" value={new Date(event.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} />
            <InfoRow label="Venue" value={event.venue} />
            {event.description && <InfoRow label="Description" value={event.description} />}
            <InfoRow label="Status" value={sc.label} />
            <InfoRow label="Indexing" value={event.indexed_status || 'not started'} />
            <InfoRow label="Published" value={event.published_at ? new Date(event.published_at).toLocaleString('en-IN') : 'Not published yet'} />
            <InfoRow label="Created" value={new Date(event.created_at).toLocaleString('en-IN')} />
          </div>
        )}

        {/* Tab: Visitors */}
        {tab === 'visitors' && (
          <div>
            {visitors.length === 0 ? (
              <Empty message="No visitors registered yet." />
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.thead}>
                    <Th>Name</Th><Th>Mobile</Th><Th>Registered At</Th><Th>Matches</Th><Th>Gallery</Th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.map(v => (
                    <tr key={v.id} style={styles.tr}>
                      <Td>{v.full_name}</Td>
                      <Td>{v.mobile_number}</Td>
                      <Td>{new Date(v.registered_at).toLocaleString('en-IN')}</Td>
                      <Td>
                        <span style={{ fontWeight: '700', color: v.match_count > 0 ? '#166534' : '#888' }}>
                          {v.match_count}
                        </span>
                      </Td>
                      <Td>
                        {v.gallery_url ? (
                          <button
                            style={styles.copyLinkBtn}
                            onClick={() => { navigator.clipboard.writeText(v.gallery_url); }}
                            title={v.gallery_url}
                          >
                            📋 Copy Link
                          </button>
                        ) : (
                          <span style={{ color: '#bbb', fontSize: '0.8rem' }}>Not published</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Tab: Photos */}
        {tab === 'photos' && (
          <div>
            {photos.length === 0 ? (
              <Empty message="No photos scanned yet. Drop photos in the FTP folder and click Scan." />
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.thead}>
                    <Th>File Name</Th><Th>Uploaded</Th><Th>Processed</Th>
                  </tr>
                </thead>
                <tbody>
                  {photos.slice(0, 100).map(p => (
                    <tr key={p.id} style={styles.tr}>
                      <Td>{p.file_name}</Td>
                      <Td>{new Date(p.uploaded_at).toLocaleString('en-IN')}</Td>
                      <Td>{p.is_processed ? '✅' : '⏳'}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Tab: Runs */}
        {tab === 'runs' && (
          <div>
            {!event.matching_runs?.length ? (
              <Empty message="No matching runs yet." />
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.thead}>
                    <Th>Run ID</Th><Th>Threshold</Th><Th>Status</Th><Th>Photos</Th><Th>Matches</Th><Th>Started</Th>
                  </tr>
                </thead>
                <tbody>
                  {event.matching_runs.map(r => (
                    <tr key={r.id} style={styles.tr}>
                      <Td>#{r.id}</Td>
                      <Td>{Math.round(r.threshold * 100)}%</Td>
                      <Td>
                        <span style={{ color: r.status === 'completed' ? '#166534' : r.status === 'failed' ? '#dc2626' : '#854d0e', fontWeight: '600' }}>
                          {r.status}
                        </span>
                      </Td>
                      <Td>{r.processed_photos}/{r.total_photos}</Td>
                      <Td>{r.total_matches}</Td>
                      <Td>{new Date(r.started_at).toLocaleString('en-IN')}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, small }) => (
  <div style={styles.statCard}>
    <div style={styles.statIcon}>{icon}</div>
    <div style={{ ...(small ? { fontSize: '1rem' } : styles.statValue) }}>{value}</div>
    <div style={styles.statLabel}>{label}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={styles.infoRow}>
    <span style={styles.infoLabel}>{label}</span>
    <span style={styles.infoValue}>{value}</span>
  </div>
);

const Th = ({ children }) => <th style={styles.th}>{children}</th>;
const Td = ({ children }) => <td style={styles.td}>{children}</td>;
const Empty = ({ message }) => <div style={styles.empty}>{message}</div>;
const LoadingScreen = () => <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>Loading...</div>;

const styles = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', system-ui, sans-serif" },
  nav: { background: '#1a1a2e', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' },
  backBtn: { background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', cursor: 'pointer', fontSize: '0.875rem' },
  navTitle: { color: '#fff', fontWeight: '700', flex: 1, fontSize: '1.05rem' },
  statusBadge: { borderRadius: '50px', padding: '0.3rem 0.875rem', fontSize: '0.8rem', fontWeight: '700' },
  container: { maxWidth: '1100px', margin: '0 auto', padding: '2rem 1.5rem' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  statCard: { background: '#fff', borderRadius: '1.25rem', padding: '1.25rem', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' },
  statIcon: { fontSize: '1.75rem', marginBottom: '0.5rem' },
  statValue: { fontSize: '2rem', fontWeight: '800', color: '#1a1a2e', lineHeight: 1 },
  statLabel: { fontSize: '0.8rem', color: '#888', marginTop: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  actions: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' },
  actionBtn: { padding: '0.6rem 1rem', background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '0.75rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', color: '#444' },
  actionBtnPrimary: { padding: '0.6rem 1.25rem', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '0.75rem', cursor: 'pointer', fontWeight: '700', fontSize: '0.875rem' },
  disabled: { opacity: 0.5, cursor: 'not-allowed' },
  scanResult: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.75rem', padding: '0.75rem 1rem', color: '#166534', fontSize: '0.875rem', marginBottom: '1rem' },
  ftpInfo: { background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '1rem', padding: '1rem 1.25rem', marginBottom: '1.5rem', fontSize: '0.875rem', color: '#555' },
  code: { background: '#f3f4f6', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.8rem', color: '#1a1a2e' },
  tabs: { display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb' },
  tab: { padding: '0.6rem 1rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', color: '#888', fontSize: '0.9rem', borderBottom: '2px solid transparent', marginBottom: '-2px' },
  tabActive: { color: '#1a1a2e', borderBottomColor: '#1a1a2e' },
  overviewGrid: { background: '#fff', borderRadius: '1.25rem', overflow: 'hidden', border: '1px solid #f0f0f0' },
  infoRow: { display: 'flex', padding: '0.875rem 1.25rem', borderBottom: '1px solid #f5f5f5', alignItems: 'flex-start', gap: '1rem' },
  infoLabel: { width: '150px', minWidth: '150px', fontSize: '0.8rem', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', paddingTop: '0.1rem' },
  infoValue: { color: '#1a1a2e', fontSize: '0.95rem' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '1.25rem', overflow: 'hidden', border: '1px solid #f0f0f0' },
  thead: { background: '#f8fafc' },
  th: { padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' },
  tr: { borderBottom: '1px solid #f5f5f5' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#444' },
  empty: { textAlign: 'center', padding: '3rem', color: '#888', background: '#fff', borderRadius: '1.25rem', border: '1px solid #f0f0f0' },
  uploadBox: { background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '1rem', padding: '1rem 1.25rem', marginBottom: '1rem' },
  uploadHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem', color: '#1a1a2e' },
  uploadDismiss: { background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '1rem', padding: '0 0.25rem' },
  progressTrack: { height: '8px', background: '#f0f0f0', borderRadius: '99px', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: '99px', transition: 'width 0.2s ease' },
  uploadErrors: { margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#dc2626' },
  copyLinkBtn: { padding: '0.3rem 0.625rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', color: '#1e40af' }
};

export default AdminEventDetail;
